import React, { useState, useEffect, useRef } from 'react';
import { loadGoogleMapsAPI, isGoogleMapsReady, getElevation } from '../../services/googleMapsService';

const GoogleAutocomplete = ({
  id,
  name,
  label,
  placeholder,
  value,
  onChange,
  required = false,
  disabled = false,
  type = 'address', // 'country', 'city', 'address'
  countryCode = '',
  cityName = '',
  errors = {},
  onValidationChange = null, // Callback pour notifier le parent du statut de validation
  onCoordinatesExtracted = null, // Callback pour récupérer les coordonnées GPS
  onAddressComponentsExtracted = null // Callback pour récupérer les composants d'adresse (ville, code postal, pays)
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedValue, setSelectedValue] = useState('');
  const [apiReady, setApiReady] = useState(false);
  const [isValidSelection, setIsValidSelection] = useState(true); // Pour suivre si la sélection est valide
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const timeoutRef = useRef(null);

  // Charger l'API Google Maps au montage du composant
  useEffect(() => {
    loadGoogleMapsAPI()
      .then(() => {
        setApiReady(true);
      })
      .catch((error) => {
        console.error('Erreur lors du chargement de l\'API Google Maps:', error);
        setApiReady(false);
      });
  }, []);

  // Notifier le parent quand le statut de validation change
  useEffect(() => {
    if (onValidationChange && type === 'address' && required) {
      onValidationChange(name, isValidSelection);
    }
  }, [isValidSelection, onValidationChange, name, type, required]);

  // Intégration avec l'API Google Places
  const getGoogleSuggestions = async (query, searchType) => {
    if (query.length < 2) return [];

    return new Promise((resolve) => {
      // Vérifier que l'API Google est chargée
      if (!apiReady || !isGoogleMapsReady()) {
        console.warn('API Google Places non initialisée');
        resolve([]);
        return;
      }

      const request = {
        input: query,
        types: [],
        componentRestrictions: {}
      };

      // Configuration selon le type de recherche
      if (searchType === 'country') {
        request.types = ['country'];
      } else if (searchType === 'city') {
        request.types = ['(cities)'];
        if (countryCode) {
          request.componentRestrictions.country = countryCode.toLowerCase();
        }
      } else if (searchType === 'address') {
        request.types = ['address'];
        if (countryCode) {
          request.componentRestrictions.country = countryCode.toLowerCase();
        }
        if (cityName) {
          request.input = `${query}, ${cityName}`;
        }
      }

      // Appel à l'API Google Places
      window.googleAutocompleteService.getPlacePredictions(request, (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          const results = predictions.slice(0, 8).map(prediction => {
            let displayDescription = prediction.description;

            // Pour les villes, extraire seulement le nom de la ville (avant la première virgule)
            if (searchType === 'city') {
              const firstCommaIndex = displayDescription.indexOf(',');
              if (firstCommaIndex > 0) {
                displayDescription = displayDescription.substring(0, firstCommaIndex);
              }
            }

            return {
              place_id: prediction.place_id,
              description: displayDescription,
              full_description: prediction.description // Garder la description complète pour référence
            };
          });
          resolve(results);
        } else {
          console.warn('Erreur API Google Places:', status);
          resolve([]);
        }
      });
    });
  };

  const handleInputChange = async (e) => {
    const inputValue = e.target.value;
    onChange(e);

    // Marquer comme invalide si l'utilisateur modifie manuellement
    // (sauf pour le type 'address' qui autorise la saisie libre en mode non-strict)
    if (type === 'address' && required) {
      setIsValidSelection(false);
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (inputValue.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    timeoutRef.current = setTimeout(async () => {
      try {
        const results = await getGoogleSuggestions(inputValue, type);
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      } catch (error) {
        console.error('Erreur autocomplétion:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setIsLoading(false);
      }
    }, 300);
  };

  const handleSuggestionClick = (suggestion) => {
    const event = {
      target: {
        name: name,
        value: suggestion.description
      }
    };
    onChange(event);
    setSelectedValue(suggestion.description);
    setIsValidSelection(true); // Marquer comme sélection valide
    setShowSuggestions(false);

    // Si c'est un pays, extraire le code pays avec l'API Google
    if (type === 'country' && window.google && window.googlePlacesService) {
      window.googlePlacesService.getDetails({
        placeId: suggestion.place_id,
        fields: ['address_components']
      }, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place.address_components) {
          const countryComponent = place.address_components.find(
            component => component.types.includes('country')
          );
          if (countryComponent) {
            inputRef.current.dataset.countryCode = countryComponent.short_name;
          }
        }
      });
    }

    // Si c'est une adresse, extraire les coordonnées GPS et les composants d'adresse
    if (type === 'address' && window.google && window.googlePlacesService) {
      window.googlePlacesService.getDetails({
        placeId: suggestion.place_id,
        fields: ['geometry', 'address_components']
      }, async (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          // Extraire les coordonnées GPS
          if (place.geometry && place.geometry.location && onCoordinatesExtracted) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();

            try {
              // Récupérer l'altitude avec l'API Elevation
              const altitude = await getElevation(lat, lng);

              // Format: long:X,lat:Y,alt:Z
              const coordinates = `long:${lng},lat:${lat},alt:${altitude}`;

              // Notifier le parent avec les coordonnées complètes
              onCoordinatesExtracted(coordinates);
            } catch (error) {
              console.warn('Erreur lors de la récupération de l\'altitude:', error);

              // Format de fallback sans altitude
              const coordinates = `long:${lng},lat:${lat},alt:0`;
              onCoordinatesExtracted(coordinates);
            }
          }

          // Extraire les composants d'adresse
          if (place.address_components && onAddressComponentsExtracted) {
            const components = place.address_components;
            let ville = '';
            let codePostal = '';
            let pays = '';

            components.forEach(component => {
              const types = component.types;

              if (types.includes('locality')) {
                ville = component.long_name;
              } else if (types.includes('postal_code')) {
                codePostal = component.long_name;
              } else if (types.includes('country')) {
                pays = component.long_name;
              }
            });

            // Notifier le parent avec les composants d'adresse
            onAddressComponentsExtracted({
              ville,
              code_postal: codePostal,
              pays
            });
          }
        }
      });
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (value !== selectedValue && type !== 'address') {
        // Réinitialiser si la valeur n'est pas sélectionnée depuis les suggestions
        const event = {
          target: {
            name: name,
            value: ''
          }
        };
        onChange(event);
      }
      setShowSuggestions(false);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="relative">
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <input
          ref={inputRef}
          type="text"
          id={id}
          name={name}
          value={value || ''}
          onChange={handleInputChange}
          onBlur={handleBlur}
          disabled={disabled}
          className={`block w-full pl-10 pr-4 py-3 border-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${
            errors[name] || (type === 'address' && required && !isValidSelection)
              ? 'border-red-400 focus:ring-red-500 bg-red-50'
              : disabled
                ? 'border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 cursor-not-allowed text-gray-500'
                : 'border-gray-200 focus:ring-blue-500 bg-white hover:border-gray-300'
          }`}
          placeholder={placeholder}
          autoComplete="off"
        />

        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        )}
      </div>

      {errors[name] && (
        <p className="text-red-500 text-sm mt-2 flex items-center">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {errors[name]}
        </p>
      )}

      {(type === 'address' && required && !isValidSelection && !errors[name]) && (
        <p className="text-orange-500 text-sm mt-2 flex items-center">
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          Veuillez sélectionner une adresse dans la liste de suggestions
        </p>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          <ul className="py-1">
            {suggestions.map((suggestion, index) => (
              <li
                key={suggestion.place_id}
                className="px-4 py-3 cursor-pointer hover:bg-blue-50 transition-colors duration-200 border-b border-gray-100 last:border-b-0"
                onMouseDown={() => handleSuggestionClick(suggestion)}
              >
                <div className="font-medium text-gray-900 text-sm">
                  {suggestion.description}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Powered by Google Places
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default GoogleAutocomplete;